import "./style.scss";

import { Avatar, CircularProgress } from "@mui/material";
import AdvanceSlicesFormBody from "components/AdvanceManagement/AdvanceSlicesFormBody";
import { resolveManagementPreviewContext } from "components/AdvanceManagement/utils/advanceSliceAmounts";
import { resolveTermAdvancePreviewTotals } from "components/AdvanceManagement/utils/advanceTpdImpact";
import { resolveManagementPreviewDisplay } from "components/AdvanceManagement/utils/managementPreviewDisplay";
import ModalBox from "components/Modal";
import AutocompleteFormComponent from "components/shared/FormComponent/AutocompleteFormComponent";
import MessageBlock from "components/shared/MessageBlock";
import StyledButton from "components/shared/StyledButton";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import TermManagementPreviewSection from "components/shared/TermManagementPreviewSection";
import { format } from "date-fns";
import { refetchManagementGeneralTab } from "helpers/refetchManagementGeneralTab";
import { BodyHighlight } from "helpers/TypographyComponents";
import { formatNumber, getCleanStartorEndDay, replaceComma, sortByProperty } from "helpers/utils";
import useAPIPagination from "hooks/useAPIPagination";
import usePaymentStateUpdate from "hooks/usePaymentStateUpdate";
import type { ChangeEvent, Dispatch, SetStateAction } from "react";
import { Fragment, useEffect, useMemo, useState } from "react";
import type { FieldArrayWithId, SubmitHandler } from "react-hook-form";
import { useFieldArray, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import type { DTOCreateAdvancePaymentSlice, DTOUpdateAdvancePayment, IAdvanceSlice } from "store/advancePaymentSlice";
import { addAdvancePayment, updateAdvancePayment } from "store/advancePaymentSlice";
import type { DTOTermPeriodData, GeneralTabTerm } from "store/managementStore";
import type { AppDispatch } from "store/store";
import { useAppSelector } from "store/store";
import { fetchAdvancePaymentsOfTerm, getTPDLinkedToTerm } from "store/termStore";
import { PaymentStatus } from "store/types";
import type { Binary } from "types";
import { v4 as uuidv4 } from "uuid";

import variables from "../../../assets/styles/_variables.module.scss";
import NewAdvanceModalSkeleton from "./NewAdvanceModalSkeleton";
import { formatInitialSlices, readTermAdvancePayments } from "./utils";

export type TSlice = {
    id: number;
    amount: string;
    netToPay: string;
    paymentDate: Date | null;
    comment: string;
    status: PaymentStatus;
    lastSentAt?: Date;
};

export type TAdvancePayment = {
    term: GeneralTabTerm | null;
    slices: {
        amount: string | number;
        paymentDate: Date | string | null;
        status: PaymentStatus;
        comment: string | null;
        key?: string | number;
        periodClosed?: Binary;
        sliceId?: number;
        paymentId?: number;
        isForfait?: Binary;
        legacyImpactBalanceN1TpdId?: number | null;
        customStatusId?: string | number | null;
    }[];
    isForfait: Binary;
    forceOver4Pass: Binary;
    newBalance?: number;
};

interface INewAdvanceModal {
    /**
     * Boolean to open modal.
     */
    openModal: boolean;
    /**
     * Triggers closing of modal (inside Term Form also unchecks the option for creating advance in Management)
     */
    onClose: (result?: { saved?: boolean }) => void;
    /**
     * Determines where the component is called from: different logic on submit.
     */
    from: "advancePage" | "termForm";
    /**
     * Needed when coming from termForm to confirm that they're entering same amount.
     */
    amountOfAdvance?: string | number;
    advancePayment?: TAdvancePayment | null;
    /**
     * Send data of advance payment for Term Container Form
     */
    setAdvancePayment?: Dispatch<SetStateAction<TAdvancePayment | null>>;
    setCreateAdvanceInManagement?: Dispatch<SetStateAction<boolean | null>>;
    /**
     * Advance payment linked to slices (needed on update).
     */
    linkedAdvancePayment?: IAdvanceSlice;
    setLinkedAdvancePayment?: Dispatch<SetStateAction<IAdvanceSlice | undefined>>;
    type?: "advance" | "package";
    /**
     * Used on term form. Needed to clear state once they submit new slices.
     */
    setAdvanceAmountChanged?: Dispatch<SetStateAction<boolean>>;
    isUpdate?: boolean;
    /**
     * Are slices passed connected to a TPD (for same termId we group slices connected and not connected).
     */
    isConnected?: boolean;
    termId?: number;
}

/**
 * Modal for creation of an advance or package. Accessed from Advance Payment page or from Term form.
 */
/**
 * TODO Refac this bunch of useEffects...
 */
function NewAdvanceModal({
    openModal,
    onClose,
    from,
    amountOfAdvance,
    setAdvancePayment,
    setCreateAdvanceInManagement,
    advancePayment,
    linkedAdvancePayment,
    type = "advance",
    setAdvanceAmountChanged,
    isUpdate = false,
    isConnected = false,
    setLinkedAdvancePayment,
}: INewAdvanceModal) {
    const [t] = useTranslation();
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const { generalTabData, termsLoadingStatus } = useAppSelector((state) => state.management);
    const { periodsDateRange, periods, activePeriod: periodId } = useAppSelector((state) => state.period);
    const [isSubmitLoading, setIsSubmitLoading] = useState(false);
    const termsLoading = termsLoadingStatus === "pending" || termsLoadingStatus === "idle";

    const { updatePaymentPageState } = usePaymentStateUpdate(periodsDateRange);
    const [totalSlicesWrong, setTotalSlicesWrong] = useState(false); // Used on edit from term form.
    const [advanceExistsOnTPD, setAdvanceExistsOnTPD] = useState(false);
    const [initialSlices, setInitialSlices] = useState<TAdvancePayment["slices"]>([]);
    const [termTpds, setTermTpds] = useState<DTOTermPeriodData[]>([]);
    const [tpdLoading, setTpdLoading] = useState(false);
    const [dataLoading, setDataLoading] = useState(isUpdate ? true : false);
    const [advanceSlicesSettled, setAdvanceSlicesSettled] = useState(!isUpdate);
    const [fetchPayments, setFetchPayments] = useState<undefined | boolean>();
    const [slicesRemoved, setSlicesRemoved] = useState<FieldArrayWithId<TAdvancePayment, "slices", "id">[]>([]);

    const isForfait = type === "package" ? 1 : (0 as Binary);

    const linkedPaymentSlices = useMemo(() => {
        const slices = (linkedAdvancePayment ?? advancePayment)?.slices ?? [];
        return slices.map((slice: IAdvanceSlice | TAdvancePayment["slices"][number]) => {
            return { ...slice, amount: (-slice.amount / 100).toString() };
        });
    }, [linkedAdvancePayment, advancePayment]);

    const defaultValues = {
        term: null,
        slices: isUpdate
            ? []
            : advancePayment && linkedPaymentSlices.length
              ? linkedPaymentSlices // When they're updating term data.
              : [
                    {
                        amount: from === "termForm" ? `${replaceComma((amountOfAdvance ?? 0).toString())}` : "",
                        paymentDate: from === "termForm" ? new Date() : null,
                        status: from === "termForm" ? PaymentStatus.PAID : PaymentStatus.NOT_PAID,
                        comment: from === "termForm" ? t("AdvancePayment.InitialAdvance") : "",
                        customStatusId: "",
                        key: uuidv4(),
                    },
                ],
        isForfait,
        forceOver4Pass:
            type === "package" && linkedAdvancePayment ? linkedAdvancePayment.forceOver4Pass : (0 as Binary),
    };

    const {
        control,
        handleSubmit,
        watch,
        formState: { errors, dirtyFields },
        setValue,
        reset,
        trigger,
    } = useForm<TAdvancePayment>({
        defaultValues: defaultValues,
        mode: "all",
    });
    const { fields, append, remove } = useFieldArray({
        name: "slices",
        control,
    });

    const { handleQuery } = useAPIPagination({
        type: "terms",
        defaultLimitPagination: 100,
        autocomplete: true,
        defaultData: { termId: linkedAdvancePayment?.termId },
    });

    const chosenTerm = watch("term");

    useEffect(() => {
        if (openModal && from === "termForm" && !linkedAdvancePayment && !advancePayment && amountOfAdvance) {
            const newSlice = [
                {
                    amount: `${amountOfAdvance.toString().replace(",", ".")}`,
                    paymentDate: new Date(),
                    status: PaymentStatus.PAID,
                    comment: t("AdvancePayment.InitialAdvance"),
                    key: uuidv4(),
                },
            ];
            setValue("slices", newSlice);
        }
    }, [openModal]);

    useEffect(() => {
        // On Updates, get term of payment to display it on field.
        if (isUpdate) {
            const shouldFetchChosenTerm = chosenTerm === null || chosenTerm.id !== linkedAdvancePayment?.termId;
            if (shouldFetchChosenTerm && generalTabData.length > 0) {
                const termData = generalTabData[0];
                setValue("term", termData);
                // Once chosenTerm set correctly, trigger fetch for linked payments.
                if (termData.id === linkedAdvancePayment?.termId) setFetchPayments(true);
            }
        } else {
            // Don't fetch payments if it's a forfait as we can create further forfait advance_payment for a term.
            if (chosenTerm && fetchPayments === undefined && !isForfait) {
                setFetchPayments(true);
            }
        }
    }, [generalTabData, chosenTerm, linkedAdvancePayment]);

    // Triggered only once chosenTerm isn't null, whether on update or creation.
    useEffect(() => {
        if (!fetchPayments) return;
        const termId = chosenTerm?.id;
        if (!termId) return;

        // Get all advances linked to term to fill out slices fields.
        dispatch(
            fetchAdvancePaymentsOfTerm({
                termId,
                typeOfPayment: type,
                typeOfAdvance: isForfait ? "disconnected" : "all",
            }),
        )
            .then(({ meta: { requestStatus }, payload }) => {
                if (requestStatus !== "fulfilled") return;
                const data = readTermAdvancePayments(payload);
                if (!data.length) return;
                const initialSlices = formatInitialSlices([...data].reverse());
                if (!initialSlices.length) return;
                setInitialSlices(initialSlices);
                reset(
                    {
                        term: chosenTerm,
                        slices: initialSlices,
                    },
                    { keepDirty: false },
                );
                setAdvanceExistsOnTPD(true);
            })
            .finally(() => {
                setAdvanceSlicesSettled(true);
            });
        if (!isForfait) {
            setTpdLoading(true);
            setTermTpds([]);
            // Load all TPD rows for the term; preview uses the contract's open period.
            dispatch(getTPDLinkedToTerm({ termId }))
                .then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        setTermTpds(resp.payload as DTOTermPeriodData[]);
                    }
                })
                .finally(() => {
                    setTpdLoading(false);
                    setDataLoading(false);
                });
        } else {
            setDataLoading(false);
        }

        setFetchPayments(false);
    }, [fetchPayments]);

    const appendRow = () => {
        append({
            amount: "",
            paymentDate: null,
            status: PaymentStatus.NOT_PAID,
            comment: "",
            customStatusId: "",
            key: uuidv4(),
        });
    };
    const removeRow = (index: number) => {
        remove(index);
        const sliceRemoved = fields[index];
        setSlicesRemoved((prev) => [...prev, sliceRemoved]);
    };

    const transformData = (data: TAdvancePayment["slices"]): DTOCreateAdvancePaymentSlice[] => {
        return data.map((slice, idx) => {
            const amount = slice.amount ?? 0;
            const paymentDate = slice.paymentDate
                ? format(getCleanStartorEndDay(slice.paymentDate), "yyyy-MM-dd")
                : null;

            return {
                ...slice,
                amount: Math.round(+amount * 100),
                paymentDate,
                id: slice.sliceId,
                sliceRow: idx,
            };
        });
    };

    const watchedSlices = useWatch({ control, name: "slices" });

    const managementPreview = useMemo(() => {
        if (from !== "advancePage") {
            const period = periods.find((p) => p.id === periodId);
            const tpd = termTpds.find((row) => row.periodId === period?.id) ?? termTpds[termTpds.length - 1];
            return { period, tpd };
        }
        return resolveManagementPreviewContext(periods, termTpds);
    }, [from, periods, periodId, termTpds]);

    const submitPeriodId = from === "advancePage" ? managementPreview.period?.id ?? periodId : periodId;

    const managementPreviewDisplay = useMemo(
        () => resolveManagementPreviewDisplay(chosenTerm, linkedAdvancePayment),
        [chosenTerm, linkedAdvancePayment],
    );

    const advancePreviewTotals = useMemo(() => {
        if (!managementPreview.period) {
            return { currentTotalCents: 0, previewTotalCents: 0 };
        }
        return resolveTermAdvancePreviewTotals(
            managementPreview.tpd,
            initialSlices,
            watchedSlices,
            managementPreview.period,
            chosenTerm?.advanceAmount,
        );
    }, [managementPreview, initialSlices, watchedSlices, chosenTerm?.advanceAmount]);

    // Wait for TPD + initial slice snapshot before showing strikethrough preview deltas on update.
    const canShowManagementPreviewDelta = !dataLoading && (!isUpdate || initialSlices.length > 0);
    const onSubmit: SubmitHandler<TAdvancePayment> = async (data) => {
        setIsSubmitLoading(true);
        try {
            const totalAmountSlices = data.slices.reduce((a, { amount }) => +amount + a, 0);
            if (isUpdate || (advanceExistsOnTPD && !isForfait)) {
                const id = chosenTerm?.id ?? linkedAdvancePayment?.termId;

                if (!id || !submitPeriodId) return;

                // Find out which slices have been updated and add the "updated" property as true.
                const dirtyIndexes = dirtyFields.slices
                    ?.map((item, index) => (item ? index : null))
                    .filter((index) => index !== null);

                data.slices.forEach((item, index) => {
                    if (dirtyIndexes?.includes(index)) {
                        item["updated"] = true;
                    }
                });

                const _data: DTOUpdateAdvancePayment = {
                    periodId: submitPeriodId,
                    slices: transformData(data.slices),
                    isForfait,
                    forceOver4Pass: +(data.forceOver4Pass ?? 0) as Binary,
                };

                const response = await dispatch(
                    updateAdvancePayment({
                        termId: id,
                        data: _data,
                    }),
                );
                if (response.meta.requestStatus === "fulfilled") {
                    updatePaymentPageState();
                    void refetchManagementGeneralTab(dispatch, submitPeriodId);
                    sessionStorage.removeItem("advance_page_data"); // Removing this so the newest slices appear even if they had selected dates.
                    onClose({ saved: true });
                }
                return;
            }

            if (from === "termForm") {
                // Check if the total amount in the slices matches the advance entered in form.
                const isTotalSlicesWrong = replaceComma((amountOfAdvance ?? 0).toString()) !== totalAmountSlices;
                setTotalSlicesWrong(isTotalSlicesWrong);

                if (isTotalSlicesWrong) {
                    return;
                }
                setAdvancePayment &&
                    setAdvancePayment({
                        term: null,
                        slices: transformData(data.slices),
                        isForfait: 0, // If I'm correct, they shouldn't be creating a forfait on term creation.
                        forceOver4Pass: 0,
                    });
                setCreateAdvanceInManagement && setCreateAdvanceInManagement(true);
                setAdvanceAmountChanged?.(false);
                updatePaymentPageState();
                sessionStorage.removeItem("advance_page_data");
                onClose();
                return;
            }

            if ((from === "advancePage" && !advanceExistsOnTPD) || isForfait) {
                if (data.term == null || !submitPeriodId) return;

                await dispatch(
                    addAdvancePayment({
                        termId: data.term.id,
                        slices: transformData(data.slices),
                        isForfait,
                        periodId: submitPeriodId,
                        forceOver4Pass: +(data.forceOver4Pass ?? 0) as Binary,
                    }),
                ).then((resp) => {
                    if (resp.meta.requestStatus === "fulfilled") {
                        updatePaymentPageState();
                        void refetchManagementGeneralTab(dispatch, submitPeriodId);
                        sessionStorage.removeItem("advance_page_data");
                        onClose({ saved: true });
                    }
                });
                return;
            }
        } finally {
            setIsSubmitLoading(false);
        }
    };

    const renderAdvanceInput = (params: any) => (
        <StyledTextField
            {...params}
            placeholder={t("AdvancePayment.BeneficiaryOrTitle")}
            onChange={(e: ChangeEvent<HTMLInputElement>) => handleQuery(e)}
            InputProps={{
                ...params.InputProps,
                endAdornment: (
                    <>
                        {termsLoading ? (
                            <CircularProgress sx={{ color: variables["primary-purple"] }} size={18} />
                        ) : null}
                        {params.InputProps.endAdornment}
                    </>
                ),
            }}
        />
    );

    const renderOptionAdvance = (props: any, option: GeneralTabTerm) => {
        return (
            <li {...props} key={option.id} className="autocomplete-advance">
                <div className="autocomplete-left-content">
                    <Avatar
                        className="autocomplete-avatar"
                        src={option.titleAvatarUrl}
                        variant={"rounded"}
                        sx={{
                            backgroundColor: "#EEF3FD",
                            color: variables["foreground"],
                            fontSize: "14px",
                            height: "40px !important",
                        }}
                    >
                        {option.title && option.title?.slice(0, 1)}
                    </Avatar>
                </div>
                <div className="autocomplete-right-content">
                    <p className="title">{option.title ?? ""}</p>
                    <p className="author">
                        {option.author ?? ""}{" "}
                        {option.representative &&
                            t("AdvancePayment.AssociatedPerson", { name: option.representative ?? "" })}
                    </p>
                </div>
            </li>
        );
    };

    const renderAdvanceText = (option: GeneralTabTerm) => {
        return `"${option?.title ?? ""}" ${t("AdvancePayment.ByAuthor")} ${option?.author ?? ""}${
            option?.representative && ` - ${option?.representative}`
        }`.trim();
    };

    const handleClose = () => {
        // When closing with invalid slices (wrong total), block next step until user fixes
        if (from === "termForm" && totalSlicesWrong) {
            setAdvanceAmountChanged?.(true);
        }
        setDataLoading(false);
        setTermTpds([]);
        setTpdLoading(false);
        setTotalSlicesWrong(false);
        setLinkedAdvancePayment?.(undefined);
        reset();
        onClose();
    };
    const handleViewInManagement = () => {
        const tpdId = managementPreview.tpd?.id ?? chosenTerm?.tpdID;
        if (!tpdId) return;
        const search = chosenTerm?.legacyTermTitleEan13?.trim() || chosenTerm?.title?.trim();
        if (search) {
            sessionStorage.setItem("management_initial_search", search);
        }
        handleClose();
        navigate("/management", {
            state: {
                tpd: tpdId,
                periodId: managementPreview.period?.id ?? periodId ?? undefined,
            },
        });
    };

    return (
        <ModalBox
            open={openModal}
            onClose={handleClose}
            mainTitle={
                linkedAdvancePayment || isUpdate
                    ? t(`AdvancePayment.ChangeAdvancePayment_${type}`)
                    : t(`AdvancePayment.AddPayment_${type}`)
            }
            subTitle={type === "advance" && isConnected ? t("BalanceUpdate.AdvancePage.WarningAdvance") : ""}
        >
            <form className="modal-form" onSubmit={handleSubmit(onSubmit)}>
                {isUpdate && (dataLoading || !advanceSlicesSettled) ? (
                    <NewAdvanceModalSkeleton
                        sliceCount={linkedAdvancePayment?.slices?.length ?? 1}
                        showManagementPreview={from === "advancePage" && type === "advance"}
                    />
                ) : (
                    <Fragment>
                        {from === "advancePage" && (
                            <AutocompleteFormComponent
                                options={generalTabData}
                                renderInput={renderAdvanceInput}
                                renderOptionText={renderAdvanceText}
                                renderOption={(props: any, option: any) => renderOptionAdvance(props, option)}
                                control={control}
                                controlName="term"
                                error={errors.term}
                                noOptionsText={t("AdvancePayment.NoOptionsText")}
                                loading={termsLoading}
                                disabled={isUpdate}
                            />
                        )}
                        {from === "termForm" && (
                            <BodyHighlight>
                                {t("BalanceUpdate.AdvancePage.TermFormSubtitle", {
                                    amount: formatNumber(+(amountOfAdvance ?? 0), { isCurrency: true }),
                                })}
                            </BodyHighlight>
                        )}
                        <AdvanceSlicesFormBody
                            formData={{ control, watch, errors, setValue, trigger }}
                            fields={fields}
                            watchedSlices={watchedSlices}
                            appendRow={appendRow}
                            removeRow={removeRow}
                            isUpdate={isUpdate || advanceExistsOnTPD}
                            advanceExistsOnTPD={advanceExistsOnTPD}
                            type={type}
                            isForfait={isForfait}
                        />
                        {from === "advancePage" &&
                            managementPreviewDisplay &&
                            !isForfait &&
                            managementPreview.period &&
                            managementPreview.tpd && (
                                <TermManagementPreviewSection
                                    display={managementPreviewDisplay}
                                    period={managementPreview.period}
                                    tpd={managementPreview.tpd}
                                    currentAdvanceAmountCents={advancePreviewTotals.currentTotalCents}
                                    previewAdvanceAmountCents={
                                        canShowManagementPreviewDelta
                                            ? advancePreviewTotals.previewTotalCents
                                            : undefined
                                    }
                                    isLoading={tpdLoading}
                                    showViewInManagement
                                    onViewInManagement={handleViewInManagement}
                                />
                            )}
                        {totalSlicesWrong && <MessageBlock type="error" text={t("ConnectTitle.ErrorAdvanceSlices")} />}
                        {slicesRemoved.length > 0 && (
                            <MessageBlock type="warning" text={t("AdvancePayment.DeleteModal.FullPayment")} />
                        )}
                        <StyledButton type="submit" fullWidth buttonType="primary" loading={isSubmitLoading}>
                            {t("AdvancePayment.ToValidate")}
                        </StyledButton>
                    </Fragment>
                )}
            </form>
        </ModalBox>
    );
}

export default NewAdvanceModal;
